<template>
  <div class="main-page-container">
    <AlertFoundActiveSession />
    <PageHeader
      :title="$t('visitSummary.title')"
      className="d-flex align-items-center justify-content-between"
    />
    <div>
      <section id="visits-summary-grid">
        <div class="d-flex datepicker-background">
          <!-- begin::Column Chooser -->
          <div>
            <button
              class="custom-columns"
              @click="dataGrid.showColumnChooser(), positionColumnChooser()"
            >
              <span class="custom-text">{{ $t("common.grid.columns") }} </span>

              <span class="custom-icon">
                <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
              </span>
            </button>
          </div>
          <!-- end::Column Chooser -->

          <!-- begin:: Export -->
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            @item-click="onItemClick"
            :text="$t('common.grid.export')"
          />

          <!-- end::Export -->

          <!-- begin::Datepicker -->
          <div>
            <div class="d-flex filter-input">
              <div class="datee mx-1">
                <date-picker
                  range
                  append-to-body
                  value-type="YYYY-MM-DD"
                  format="YYYY-MM-DD"
                  placeholder="Filter by date"
                  :clearable="false"
                  @open="selectedDate = null"
                  @pick="pickDate($event)"
                  :disabled-date="disableDate"
                  range-separator=""
                  ref="datePick"
                  :class="{
                    'date-noDataWidth': !dateRange[0],
                    'date-dataWidth': !!dateRange[0],
                  }"
                  v-model="dateRangeLocal"
                  @change="dateChanged()"
                >
                  <i slot="icon-calendar" class="mdi mdi-calendar"> </i>
                </date-picker>
              </div>
              <div class="center-icon d-flex h-30">
                <div
                  class="date-close-style d-flex align-items-center vp-ml-8"
                  v-if="dateRange && checkDate"
                  @click="resetDate()"
                >
                  <svg
                    style="cursor: pointer"
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                      fill="#F46A6A"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <!-- end::Datepicker -->

          <!-- begin::Clear all -->
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
          <!-- end::Clear all -->
        </div>
        <DxDataGrid
          :ref="dataGridRef"
          :allow-column-reordering="true"
          :data-source="dataSource"
          :showBorders="false"
          :columnAutoWidth="true"
          :allowColumnResizing="true"
          :rowAlternationEnabled="false"
          :showColumnLines="true"
          :class="{
            summaryGridHeightWithAlert: getActiveSessionsCount,
            summaryGridHeight: !getActiveSessionsCount,
          }"
          @content-ready="onContentReady"
          @data-error-occurred="onDataErrorOccurred($event)"
          :rtl-enabled="rtlEnabled"
          :no-data-text="$t('common.grid.noDataYet')"
        >
          <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />

          <DxLoadPanel :enabled="true"> </DxLoadPanel>
          <DxFilterRow :visible="true" />
          <DxHeaderFilter :visible="true"/>
          <DxColumnChooser mode="select" :enabled="true" :title="$t('common.grid.columnChooser')" />

          <DxColumn
            data-field="fullName"
            :allow-header-filtering="false"
            cell-template="fullName-cell"
            :allow-filtering="true"
            :filter-operations="allowedOperations"
            :caption="$t('common.fullName')"
          />
          <DxColumn
            data-field="userType"
            :allow-filtering="false"
            :allow-header-filtering="true"
            :caption="$t('common.userType')"
          />
          <DxColumn
            data-field="signedIn"
            :allow-filtering="false"
            :caption="$t('visitSummary.grid.signInTime')"
            data-type="datetime"
            css-class="signed-in-column"
            :customize-text="signedInOutTimeFormat"
          />
          <DxColumn
            data-field="signedOut"
            :allow-filtering="false"
            css-class="signed-out-column"
            :caption="$t('visitSummary.grid.signOutTime')"
            data-type="datetime"
            :customize-text="signedInOutTimeFormat"
          />
          <DxColumn
            data-field="duration"
            css-class="duration-column"
            :allow-filtering="false"
            :caption="$t('common.duration')"
          />
          <DxColumn
            data-field="site"
            :allow-filtering="false"
            :allow-header-filtering="true"
            :caption="$t('common.site')"
          />
          <DxColumn
            data-field="area"
            :allow-filtering="false"
            :allow-header-filtering="true"
            :caption="$t('common.area')"
          />
          <DxColumn
            data-field="signedInBy"
            :allow-filtering="false"
            :caption="$t('visitSummary.grid.signedInBy')"
          />
          <DxColumn
            data-field="signedInNotes"
            :allow-filtering="false"
            :caption="$t('visitSummary.grid.signInNotes')"
            css-class="max-width-style"
            cell-template="notes-cell"
          />
          <template #notes-cell="{ data }">
            <div style="white-space: normal; overflow-wrap: break-word">
              <template v-if="data.value && !readMore[data.value]">
                {{ data.value.substring(0, 21) }}
              </template>

              <template v-if="readMore[data.value]">
                {{ data.value }}
              </template>

              <a
                class="read_more"
                style="cursor: pointer"
                v-if="
                  !readMore[data.value] && data.value && data.value.length >= 21
                "
                @click="showMore(data.value)"
                >{{ $t('common.readMore') }}</a
              >
              <a
                class="read_more"
                style="cursor: pointer"
                v-if="readMore[data.value]"
                @click="showLess(data.value)"
                >{{ $t('common.readLess') }}</a
              >
            </div>
          </template>
          <DxColumn
            data-field="signedOutBy"
            :caption="$t('visitSummary.grid.signedOutBy')"
            :allow-filtering="false"
          />
          <DxColumn
            data-field="signedOutNotes"
            :allow-filtering="false"
            :caption="$t('visitSummary.grid.signOutNotes')"
            cell-template="sign-out-notes-cell"
          />
          <template #sign-out-notes-cell="{ data }">
            <div style="white-space: normal; overflow-wrap: break-word">
              <template v-if="data.value && !readMore[data.value]">
                {{ data.value.substring(0, 21) }}
              </template>

              <template v-if="readMore[data.value]">
                {{ data.value }}
              </template>

              <a
                class="read_more"
                style="cursor: pointer"
                v-if="
                  !readMore[data.value] && data.value && data.value.length >= 21
                "
                @click="showMore(data.value)"
                >{{ $t('common.readMore') }} </a
              >
              <a
                class="read_more"
                style="cursor: pointer"
                v-if="readMore[data.value]"
                @click="showLess(data.value)"
                >{{ $t('common.readLess') }}</a
              >
            </div>
          </template>
          <DxColumn
            data-field="email"
            :allow-header-filtering="false"
            :allow-filtering="true"
            :caption="$t('common.emailAddress')"
            :filter-operations="allowedOperations"
          />
          <DxColumn
            data-field="phone"
            :allow-header-filtering="false"
            :allow-filtering="true"
            :caption="$t('common.phoneNumber')"
            :filter-operations="allowedOperations"
          />
          <DxColumn
            :allow-header-filtering="false"
            :allow-filtering="true"
            :caption="$t('common.RFID')"
            :filter-operations="allowedOperations"
            :min-width="150"
            :allow-sorting="false"
            :calculate-cell-value="rfidStore"
            :customize-text="rfidCustomizeText"
          />

          <DxColumn
            data-field="id"
            :allow-header-filtering="false"
            :allow-filtering="true"
            :visible="false"
            :caption="$t('common.id')"
          />
          <DxSummary>
            <DxGroupItem
              column="duration_seconds"
              summary-type="sum"
              :customize-text="durationFormat"
            />
          </DxSummary>
          <DxGroupPanel
            :visible="true"
            :empty-panel-text="$t('common.grid.groupPanelMessage')"
          />
          <DxGrouping :auto-expand-all="autoExpandAll" />
          <DxPaging :page-size="10" />
          <DxSearchPanel :visible="false" />
          <DxPaging :page-size="50" />
          <DxPager
            :visible="true"
            :allowed-page-sizes="[5, 10, 25, 50, 100]"
            :display-mode="'full'"
            :show-page-size-selector="true"
            :show-info="true"
            :show-navigation-buttons="true"
            :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
          />
          <DxStateStoring
            :enabled="false"
            type="custom"
            :customLoad="customLoad"
            :customSave="customSave"
            storage-key="visitsSummaryGrid"
          />
          <template #fullName-cell="{ data }">
            <div class="bold-name">{{ data.value }}</div>
          </template>
        </DxDataGrid>
      </section>
    </div>
  </div>
</template>

<script>
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { default as Button } from "devextreme-vue/button";
import PageHeader from "@/components/page-header";
import { rfidFormatterMixin } from "@/utils/mixins/rfidFormatterMixin";

import {
  DxDataGrid,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxSearchPanel,
  DxHeaderFilter,
  DxColumnChooser,
  // DxItem,
  DxFilterRow,
  DxSummary,
  DxPaging,
  DxGroupItem,
  DxStateStoring,
  // DxToolbar,
  DxPager,
  DxLoadPanel,
  DxScrolling,
} from "devextreme-vue/data-grid";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import "devextreme/data/odata/store";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";

const dataGridRef = "my-data-grid";
export default {
  title: "Visit Summary",
  mixins: [rfidFormatterMixin],
  components: {
    DxColumn,
    PageHeader,
    DxLoadPanel,
    Button,
    DxGroupPanel,
    DxGrouping,
    DxPaging,
    DxSearchPanel,
    DxSummary,
    DxHeaderFilter,
    DxColumnChooser,
    // DxItem,
    DxGroupItem,
    DxDataGrid,
    DxFilterRow,
    DxPager,
    DxStateStoring,
    // DxToolbar,
    DxDropDownButton,
    AlertFoundActiveSession,
    DxScrolling,
  },
  data() {
    return {
      rtlEnabled: false,
      userTimezone: moment.tz.guess(),
      title: "Visit Summary",
      buttonData: ["Excel", "CSV"],
      dataGridRef,
      autoExpandAll: false,
      loaded: false,
      readMore: {},
      dateRange: [
        moment(new Date().setHours(0, 0, 0, 0)).utc().format("YYYY-MM-DD"),
        moment(new Date().setHours(23, 59, 59, 999)).utc().format("YYYY-MM-DD"),
      ],
      dateRangeLocal: [
        moment
          .utc(new Date(Date.now()))
          .tz(moment.tz.guess())
          .format("YYYY-MM-DD"),
        moment
          .utc(new Date(Date.now()))
          .tz(moment.tz.guess())
          .format("YYYY-MM-DD"),
      ],
      multipleFilters: [
        {
          Date: {
            start: moment(new Date().setHours(23, 59, 59, 999))
              .utc()
              .format("YYYY-MM-DD"),
            end: moment(new Date().setHours(0, 0, 0, 0))
              .utc()
              .format("YYYY-MM-DD"),
          },
        },
      ],
      allowedOperations: ["contains"],
      showClearAll: false,
    };
  },
  mounted() {
  try {
    // Enable RTL if language is Arabic
    this.rtlEnabled = this.getLanguage === "ar";

    // Ensure dateRange is valid
    if (this.dateRange && this.dateRange.length === 2) {
      this.$store.dispatch("visit/fetchVisitsSummary", {
        start_date: this.dateRange[0],
        end_date: this.dateRange[1],
      });
    } else {
      console.warn("Invalid date range: ", this.dateRange);
    }
  } catch (error) {
    console.error("Error in mounted lifecycle:", error);
  }
},
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("visit", ["getVisitsSummary"]),
    ...mapGetters("grid", ["getTableHeaders"]),
    ...mapGetters("visit", ["getVisitsSummary"]),
    ...mapGetters("authfack", ["userDetails"]),
    ...mapGetters("emergency", ["getActiveSessionsCount"]),
    dataGrid() {
      return this.$refs[this.dataGridRef].instance;
    },
    dataSource() {
      return this.getVisitsSummary;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    checkDate() {
      if (
        this.dateRangeLocal.length > 0 &&
        this.dateRangeLocal[0] ==
          moment
            .utc(new Date(Date.now()))
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD") &&
        this.dateRangeLocal[1] ==
          moment
            .utc(new Date(Date.now()))
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD")
      )
        return false;
      return true;
    },
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "createTableHeaders",
      "updateTableHeaders",
    ]),
    rfidStore(e) {
      return e.rf_ids?.length ? this.rfidFormatter(e.rf_ids) : null;
    },
    rfidCustomizeText(cellInfo) {
      return cellInfo.value ?? " - ";
    },
    signedInOutTimeFormat(e) {
      if (moment(e.value).isValid()) {
        if(this.getLanguage === "ar"){
          return moment(e.value)
          .locale('ar_SA')
          .utc(e.value)
          .tz(this.userTimezone)
          .format("DD/MM/YYYY - hh:mm:ss A");
        }else{
          return moment(e.value)
          .utc(e.value)
          .tz(this.userTimezone)
          .format("DD/MM/YYYY - hh:mm:ss A");
        }
      }
      return e.valueText;
    },
    onContentReady(e) {
      // console.log(e);
      // detect group columns applied in columns
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      let columns = e.component.getVisibleColumns();
      let groupingIsNotAppliedColumns = true;
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].groupIndex !== undefined) {
          groupingIsNotAppliedColumns = false;
          break;
        }
      }
      //Add class to row
      if (!groupingIsNotAppliedColumns) {
        const rows = document.getElementsByClassName(
          "dx-row dx-column-lines dx-group-row"
        );
        for (let item of rows) {
          item.classList.add("custom-row-selector");
          // item.setAttribute("style", "color:brown !important");
        }
      }
      //Set placeholder for 'search' input on grid
      let el = e.element.querySelectorAll(".dx-texteditor-input");
      el.forEach((filterInput) => {
        filterInput.placeholder = this.$t("common.grid.search");
        filterInput.style.marginTop = "1px";
      });
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    pickDate(e) {
      this.selectedDate = e;
    },
    disableDate(date) {
      return this.selectedDate
        ? date > new Date(this.selectedDate).getTime() + 7.884e9 ||
            date < new Date(this.selectedDate).getTime() - 7.884e9
        : false; // 3 months in milliseconds
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
      // this.resetDate();
    },
    positionColumnChooser() {
      const columnChooserView = this.dataGrid.getView("columnChooserView");

      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }

      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 10",
      });
    },
    onItemClick(e) {
      if (e.itemData == "Excel") this.exportXLSX();
      else if (e.itemData == "CSV") this.exportCSV();
      // else if (e.itemData == "PDF") this.exportPDF();
    },
    exportXLSX() {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Main sheet");
      exportDataGrid({
        component: this.dataGrid,
        worksheet,
        autoFilterEnabled: true,
        // topLeftCell: { row: 2, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          if (gridCell.rowType === "data") {
            if (gridCell.column.dataField === "Image" && gridCell.value) {
              excelCell.value = undefined;
              const image = workbook.addImage({
                base64: gridCell.value,
                extension: "png",
              });
              worksheet.getRow(excelCell.row).height = 90;
              worksheet.addImage(image, {
                tl: { col: excelCell.col - 1, row: excelCell.row - 1 },
                br: { col: excelCell.col, row: excelCell.row },
              });
            }
          }
        },
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Visits Summary.xlsx"
          );
          this.isExport = false;
        });
      });
    },
    exportCSV() {
      this.dataGrid.beginUpdate();
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Main sheet");

      exportDataGrid({
        component: this.dataGrid,
        worksheet: worksheet,
      }).then(() => {
        workbook.csv.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Visits Summary.csv"
          );
          this.dataGrid.endUpdate();
        });
      });
    },
    resetDate() {
      this.dateRange = [
        moment(new Date().setHours(0, 0, 0, 0)).utc().format("YYYY-MM-DD"),
        moment(new Date().setHours(23, 59, 59, 999)).utc().format("YYYY-MM-DD"),
      ];
      this.dateRangeLocal = [
        moment
          .utc(new Date(Date.now()))
          .tz(moment.tz.guess())
          .format("YYYY-MM-DD"),
        moment
          .utc(new Date(Date.now()))
          .tz(moment.tz.guess())
          .format("YYYY-MM-DD"),
      ];
      this.dateChanged();
    },
    dateChanged() {
      if (this.dateRangeLocal[0])
        this.dateRange[0] = moment(new Date(this.dateRangeLocal[0]))
          .utc()
          .format("YYYY-MM-DD");
      if (this.dateRangeLocal[1])
        this.dateRange[1] = moment(new Date(this.dateRangeLocal[1]))
          .utc()
        .format("YYYY-MM-DD");
      if (this.dateRangeLocal.length == 0 || !this.dateRangeLocal[0]) {
        (this.dateRangeLocal = [
          moment
            .utc(new Date(Date.now()))
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD"),
          moment
            .utc(new Date(Date.now()))
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD"),
        ]),
          (this.dateRange = [
            moment(new Date().setHours(0, 0, 0, 0)).utc().format("YYYY-MM-DD"),
            moment(new Date().setHours(23, 59, 59, 999))
              .utc()
              .format("YYYY-MM-DD"),
          ]);
      }
      this.$store.dispatch(
        "visit/fetchVisitsSummary",
        { start_date: this.dateRange[0], end_date: this.dateRange[1] },
        { root: true }
      );
    },

    durationFormat(e) {
      // return moment.utc(parseInt(e.value, 10)*1000).format(`HH [${this.$t('common.timeFormat.h')}]:mm [${this.$t('common.timeFormat.m')}]:ss [${this.$t('common.timeFormat.s')}]`);
      const sec = parseInt(e.value, 10); // convert value to number if it's string
      let hours = Math.floor(sec / 3600); // get hours
      let minutes = Math.floor((sec - hours * 3600) / 60); // get minutes
      let seconds = sec - hours * 3600 - minutes * 60; //  get seconds
      hours = hours == 0 ? "" : `${hours}${this.$t('common.timeFormat.h')} `;
      minutes = minutes == 0 ? "" : `${minutes}${this.$t('common.timeFormat.m')} `;
      seconds = seconds == 0 ? "" : `${seconds}${this.$t('common.timeFormat.s')}`;
      return `${this.$t('common.totalDuration')} : ${hours} ${minutes} ${seconds} `; // Return is HH : MM : SS
    },
    async customLoad() {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/reporting/visit_summary",
        });
        let value = this.getTableHeaders;

        if (value) {
          value.pageIndex = 0;
          value.pageSize = 50;
        }
        this.loaded = true;
        return value;
      }
      this.loaded = true;
    },

    customSave(gridState) {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;

        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");

        if (this.loaded && !this.getTableHeaders) {
          this.createTableHeaders({
            url: "/reporting/visit_summary",
            headers: value,
            filters: "[]",
          });
        } else if (
          this.loaded &&
          value != JSON.stringify(this.getTableHeaders)
        ) {
          this.updateTableHeaders({
            url: "/reporting/visit_summary",
            headers: value,
            filters: "[]",
          });
        }
      }
    },
    showMore(id) {
      this.$set(this.readMore, id, true);
    },
    showLess(id) {
      this.$set(this.readMore, id, false);
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-datagrid-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
};
</script>
<style lang="scss">
.signed-in-column,.signed-out-column ,.duration-column{
  direction: ltr !important;
}
.main-page-container .dx-rtl .dx-treeview-node-container .dx-treeview-node, .dx-rtl .dx-treeview-node-container .dx-treeview-node.dx-treeview-item-with-checkbox .dx-treeview-item, .dx-rtl .dx-treeview-node-container:first-child>.dx-treeview-node{
  padding-left: 10px;
}
.summaryGridHeight .dx-datagrid {
  height: calc(100vh - 272px);
}
.summaryGridHeightWithAlert .dx-datagrid {
  height: calc(100vh - 318px);
}

.max-width-style {
  max-width: 350px;
}
.read_more {
  width: 69px;
  height: 17px;
  inset-inline-start: 721px;
  top: 503px;

  font-weight: 700;
  font-size: 14px;
  line-height: 23.5px;
  text-decoration-line: underline !important;
  color: #2680eb;
}
section#visits-summary-grid {
  td.dx-group-cell {
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 19px !important;

    color: #495057 !important;
  }
  .dx-widget.dx-datagrid-pager.dx-pager {
    padding: 13px 40px !important;
  }
  .dx-toolbar-after {
    display: none;
  }
  .dx-datagrid .dx-sort-down::before {
    content: "\f052";
    color: #939393;
    font-size: 16px;
    font-weight: 700;
  }
  .dx-datagrid .dx-header-filter::before {
    content: "\f050";
    color: #939393;
    font-weight: 700;
  }
  .dx-datagrid-group-opened::before {
    content: "\f016";
    color: #636161;
    font-size: 16px;
    font-weight: 700;
  }
  .dx-datagrid .dx-sort-up::before {
    font-family: DXIcons, sans-serif;
    color: #939393;
    font-size: 16px !important;
    font-weight: 700 !important;
  }
  .dx-datagrid-content
    .dx-datagrid-table
    [class*="column"]
    + [class*="column"]:last-child {
    height: 37px;
  }
  .custom-row-style {
    background: #ffffff;

    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #495057;
    .bold-name.dx-template-wrapper {
      font-weight: 400 !important;
      font-size: 16px !important;
      line-height: 19px !important;
      text-decoration-line: none;
      color: #495057 !important;
    }
  }

  .dx-datagrid-rowsview .dx-row.dx-group-row {
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 19px !important;

    color: #495057 !important;
  }
  td.dx-cell-focus-disabled.dx-master-detail-cell {
    background: white !important;
    padding-inline-start: 0 !important;
  }
  tbody {
    color: #495057;
  }

  .dx-toolbar-items-container {
    display: flex;
  }

  .filter-input {
    justify-items: center;
    align-items: center;
  }

  .datepicker-background {
    background: #ffffff;
    border-radius: 8px 8px 0px 0px;
    // margin-bottom: 2px;
    height: 75px;
    align-items: center;
  }

  .dashboard-gridd {
    @media only screen and (max-width: 700px) {
      font-size: 0.6rem !important;
    }
  }
  .export-dropdown {
    height: 27px;
  }
  .dx-dropdownbutton {
    padding: 0;
    margin: 0;
    background: #277fea;
    // margin-inline-start: 32px !important;
    // margin-inline-end: 20px !important ;
    width: 80px;
    height: 27px !important;
    border: none;
    outline: none;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    .dx-icon {
      color: #ffffff;
    }
  }
}
.datee {
  .date-noDataWidth {
    .mx-input {
      width: 134px !important;
      padding: 0 6px;
    }
  }
  .date-dataWidth {
    .mx-input {
      width: 226px !important;
    }
  }
  .mx-datepicker {
    .mx-icon-calendar {
      color: #ffffff !important;
    }
    .mdi {
      color: #ffffff !important;
    }
    .mx-input::placeholder {
      color: #ffffff !important;

      font-weight: 400 !important;
      font-size: 16px !important;
      line-height: 19px !important;
    }
    .mx-input {
      height: 27px !important ;
      top: 34.11% !important;
      bottom: 62.89% !important;
      // padding: 0 5px !important;
      background: #2680eb !important;
      border-color: #2680eb !important;
      border-radius: 4px !important;
      color: #ffffff !important;
      font-weight: 400 !important;
      font-size: 16px !important;
      line-height: 19px !important;
    }
  }
}

[aria-label="Column RFID"] {
  .dx-sort.dx-sort-up,
  .dx-sort.dx-sort-down {
    display: none !important;
  }
}
</style>
